import { Edit } from '@mui/icons-material'
import { Button, Card, CardContent, CircularProgress, Divider, Grid, Typography } from '@mui/material'
import { GreenText, LabelField } from 'components'
import * as controllers from 'controllers'
import { VehicleCheckInDetailsDto, VehicleDto } from 'dtos'
import { VehicleClusterInformationDto } from 'dtos/vehicleClusterInformationDto'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { notNullOrUndefined } from 'utils'
import ClusterInformationAddEditDialog from './ClusterInformationAddEditDialog'

interface ClusterInformationListCardProps {
  vehicle: VehicleDto
}

export default function ClusterInformationListCard({ vehicle }: ClusterInformationListCardProps) {
  const [isSavingClusterInformation, setIsSavingClusterInformation] = useState<boolean>(false)
  const [isGettingClusterInformation, setIsGettingClusterInformation] = useState<boolean>(false)
  const [clusterInformationAddEditDialogOpen, setClusterInformationAddEditDialogOpen] = useState<boolean>(false)
  const [clusterInformation, setClusterInformation] = useState<VehicleClusterInformationDto>(new VehicleClusterInformationDto())
  const [vehicleDetails, setVehicleDetails] = useState<VehicleCheckInDetailsDto>(new VehicleCheckInDetailsDto())

  useEffect(() => {
    if (vehicle.vehicleGuid) {
      setIsGettingClusterInformation(true)
      controllers
        .getClusterInformation(vehicle.vehicleGuid!)
        .then(response => {
          setClusterInformation(response.value)
        })
        .finally(() => {
          setIsGettingClusterInformation(false)
        })
    }
  }, [vehicle])

  useEffect(() => {
    if (vehicle.vehicleGuid) {
      setIsGettingClusterInformation(true)
      controllers
        .getVehicleCheckInDetails(vehicle.vehicleGuid!)
        .then(response => {
          setVehicleDetails(response.value)
        })
        .finally(() => {
          setIsGettingClusterInformation(false)
        })
    }
  }, [vehicle])

  return (
    <>
      <ClusterInformationAddEditDialog
        initialValues={clusterInformation}
        isLoading={isSavingClusterInformation}
        onClose={() => {
          setClusterInformationAddEditDialogOpen(false)
        }}
        onSave={values => {
          setIsSavingClusterInformation(true)
          controllers
            .updateClusterInformation(vehicle.vehicleGuid!, values)
            .then(response => {
              if (response?.value) {
                setClusterInformation(response.value)
              }

              setClusterInformationAddEditDialogOpen(false)
              enqueueSnackbar('Cluster Information Saved Successfully!', { variant: 'success' })
            })
            .finally(() => {
              setIsSavingClusterInformation(false)
            })
        }}
        open={clusterInformationAddEditDialogOpen}
      />
      <Card variant='outlined'>
        <CardContent>
          <Grid container spacing={1} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm='auto'>
                <Typography fontWeight='bold' variant='h5'>
                  Cluster Information
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto'>
                <Button
                  onClick={() => {
                    setClusterInformationAddEditDialogOpen(true)
                  }}
                  variant='text'
                  endIcon={<Edit />}
                >
                  EDIT
                </Button>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {isGettingClusterInformation && (
              <Grid item container alignItems='center' justifyContent='center' sx={{ my: 1 }}>
                <Grid>
                  <CircularProgress color='primary' size={50} />
                </Grid>
              </Grid>
            )}

            {!isGettingClusterInformation && (
              <>
                <Grid item xs={12}>
                  <LabelField
                    label='Vehicle Check-In Odometer Reading'
                    value={clusterInformation.vehicleCheckInOdometerReadingMileage?.toString()}
                  />
                </Grid>

                <Grid item xs={12}>
                  <LabelField label='Convert Cluster' value={clusterInformation.convertCluster ? 'Yes' : 'No'} />
                </Grid>

                <Grid item xs={12}>
                  <LabelField
                    label='Cluster Conversion In-House or 3rd Party'
                    value={clusterInformation.clusterConversionInHouseOrThirdParty}
                  />
                </Grid>

                <Grid item xs={12}>
                  <LabelField
                    label='3rd Party Cluster Conversion Company'
                    value={clusterInformation.thirdPartyClusterConversionCompany?.name}
                  />
                </Grid>

                <Grid item xs={12}>
                  <LabelField label='Additional Programming' value={clusterInformation.additionalProgramming ? 'Yes' : 'No'} />
                </Grid>

                <Grid item xs={12}>
                  <LabelField
                    label='Additional Programming In-House or 3rd Party'
                    value={clusterInformation.additionalProgrammingInHouseOrThirdParty}
                  />
                </Grid>

                <Grid item xs={12}>
                  <LabelField
                    label='3rd Party Additional Programming Company'
                    value={clusterInformation.thirdPartyAdditionalProgrammingCompany?.name}
                  />
                </Grid>

                <Grid item xs={12}>
                  <LabelField
                    label='Current Odometer Reading (After Cluster Reinstallation Complete)'
                    value={
                      notNullOrUndefined(clusterInformation.clusterReinstallationOdometerReadingMi)
                        ? clusterInformation.clusterReinstallationOdometerReadingMi + ' MI'
                        : undefined
                    }
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`**EDIT Button:**

Clicking the EDIT button opens the Edit Cluster Information dialog.

**Vehicle Check-In Odometer Reading:**

Displays the vehicle's odometer reading, as entered in the Edit Cluster Information dialog.

If no value has been entered for the Vehicle Check-In Odometer Reading, a “-” will be displayed.

**Convert Cluster:**

Displays "Yes" or "No" to indicate if the vehicle's cluster needs to be converted as selected in the Edit Cluster Information dialog.

**Cluster Conversion In-House or 3rd Party:**

Displays "In-House" or "3rd Party" to indicate if the cluster conversion will be performed In-House or by a 3rd Party, as selected in the Edit 
Cluster Information dialog.

**3rd Party Cluster Conversion Company:**

Displays the name of the 3rd Party Cluster Conversion Company entered in the Edit Cluster Information dialog.

If no value has been entered for the 3rd Party Cluster Conversion Company, a “-” will be displayed.

**Additional Programming:**

Displays "Yes" or "No" to indicate if additional programming is required, as selected in the Edit Cluster Information dialog.

**3rd Party Additional Programming Company:**

Displays the name of the 3rd Party Additional Programming Company entered in the Edit Cluster Information dialog.

If no value has been entered for the 3rd Party Additional Programming Company, a “-” will be displayed.

**Current Odometer Reading (After Cluster Reinstallation Complete):**

Displays the current odometer reading after cluster reinstallation, as entered in the Edit Cluster Information dialog.

If no value has been entered for the vehicle's current odometer reading after Cluster Reinstallation, a “-” will be displayed.`}
              </GreenText>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
